import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
// Adjust import path as needed
import CampaignsWithoutScopeTable from "./CampaignsWithoutScopeTable/CampaignsWithoutScopeTable";
import NegativesInAdgroupTable from "./NegativeSTInAdgroup/NegativesInAdgroupTable";
import NegativesInCampaignTable from "./NegativeSTInCampaign/NegativesInCampaignTable";
import {
  resetNegativesInAdGroupTableFilters,
  resetNegativesInCampaignTableFilters,
} from "../../../../../../../../../features/scopesTabSlice";

// Shared accordion styles to eliminate repetition
const accordionStyles = {
  "&.Mui-expanded": {
    margin: 0,
  },
  "& .MuiAccordionSummary-root": {
    minHeight: "48px",
  },
  "& .MuiAccordionSummary-root.Mui-expanded": {
    minHeight: "48px",
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    margin: 0,
  },
};

interface AccordionItem {
  id: string;
  title: string;
  description: string;
  dataKey:
    | "campaignsWithoutScope"
    | "negativesInListAndAdGroup"
    | "negativesInCampaign";
  component: React.ComponentType;
  clearAction?: () => any; // Redux action creator to clear filters
  urlParamsToClear?: string[]; // URL params to clear for this accordion
  urlKey: string; // Key to set in URL when accordion expands
}

const Stonomalies = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const data = useSelector((state: any) => ({
    campaignsWithoutScope: state.searchTermOperation.campaignsWithoutScopeData,
    negativesInListAndAdGroup:
      state.searchTermOperation.negativesInListAndInAdGroupData,
    negativesInCampaign: state.searchTermOperation.negativesInTheCampaignData,
  }));

  const [expanded, setExpanded] = useState<string | false>(false);

  // Configuration for accordion items
  const accordionItems: AccordionItem[] = [
    {
      id: "panel1",
      title: "Campaigns without scope",
      description: "All enabled Campaigns that are not assigned to any scope",
      dataKey: "campaignsWithoutScope",
      component: CampaignsWithoutScopeTable,
      urlKey: "campaignsWithoutScope",
      urlParamsToClear: ["filters", "search"],
    },
    {
      id: "panel2",
      title: "Negative definition in both list and adgroup",
      description: "Search terms with duplication of negative definitions",
      dataKey: "negativesInListAndAdGroup",
      component: NegativesInAdgroupTable,
      clearAction: () => dispatch(resetNegativesInAdGroupTableFilters()),
      urlKey: "negativesInAdgroup",
      urlParamsToClear: ["filters", "page", "sort", "search"],
    },
    {
      id: "panel3",
      title: "Negative definition in campaign",
      description:
        "Search terms with negative definition at the campaign level (bad practice)",
      dataKey: "negativesInCampaign",
      component: NegativesInCampaignTable,
      clearAction: () => dispatch(resetNegativesInCampaignTableFilters()),
      urlKey: "negativesInCampaign",
      urlParamsToClear: ["filters", "page", "sort", "search"],
    },
  ];

  // Function to handle accordion expansion/collapse
  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      const accordionItem = accordionItems.find((item) => item.id === panel);

      if (isExpanded && accordionItem) {
        // Opening a new accordion
        const newParams = new URLSearchParams(searchParams);

        // If another accordion was open, clear its filters and URL first
        if (expanded && expanded !== panel) {
          const previousAccordion = accordionItems.find(
            (item) => item.id === expanded
          );

          if (previousAccordion) {
            // Clear previous accordion's URL params
            if (previousAccordion.urlParamsToClear) {
              previousAccordion.urlParamsToClear.forEach((key) => {
                if (newParams.has(key)) {
                  newParams.delete(key);
                }
              });
            }

            // Dispatch clear action for previous accordion
            if (previousAccordion.clearAction) {
              dispatch(previousAccordion.clearAction());
            }
          }
        }

        // Set new accordion in URL
        newParams.set("accordion", accordionItem.urlKey);
        setSearchParams(newParams);
        setExpanded(panel);
      } else if (!isExpanded && expanded === panel && accordionItem) {
        // Closing an accordion - remove from URL and clear filters
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("accordion");

        // Clear specific URL params for this accordion
        if (accordionItem.urlParamsToClear) {
          accordionItem.urlParamsToClear.forEach((key) => {
            if (newParams.has(key)) {
              newParams.delete(key);
            }
          });
        }

        setSearchParams(newParams);

        // Dispatch clear action if defined for this panel
        if (accordionItem.clearAction) {
          dispatch(accordionItem.clearAction());
        }

        setExpanded(false);
      }
    };

  // Set expanded accordion from URL
  useEffect(() => {
    const accordionParam = searchParams.get("accordion");
    if (accordionParam) {
      const accordionItem = accordionItems.find(
        (item) => item.urlKey === accordionParam
      );
      if (accordionItem) {
        setExpanded(accordionItem.id);
      }
    } else {
      // If no accordion param in URL, collapse all
      setExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="column"
        justifyContent="space-between"
        alignItems="left"
        mt={1}
        mb={1}
      >
        <Typography variant="h5">Anomalies</Typography>
        <Typography variant="body2" gutterBottom color="text.secondary">
          Identify and resolve account-level anomalies in Negative Search Term
          configurations
        </Typography>
      </Stack>

      {/* Dynamic Accordions */}
      {accordionItems.map(
        ({ id, title, description, dataKey, component: Component }) => {
          const count = data[dataKey]?.length || 0;

          return (
            <Accordion
              key={id}
              expanded={expanded === id}
              onChange={handleChange(id)}
              sx={accordionStyles}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${id}-content`}
                id={`${id}-header`}
                sx={{ minHeight: "48px !important" }}
              >
                <Typography
                  component="span"
                  sx={{ width: "33%", flexShrink: 0 }}
                >
                  {title} – {count}
                </Typography>
                <Typography component="span" sx={{ color: "text.secondary" }}>
                  {description}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Component />
              </AccordionDetails>
            </Accordion>
          );
        }
      )}
    </Box>
  );
};

export default Stonomalies;
